import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  deleteDoc,
  query, 
  where, 
  getDocs,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../config/firebase';

const COLLECTION = 'users';

/**
 * Criar um novo usuário no Firestore
 */
export const createUser = async (userId, userData) => {
  try {
    const userRef = doc(db, COLLECTION, userId);
    await setDoc(userRef, {
      ...userData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Error creating user:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Buscar usuário por ID
 */
export const getUserById = async (userId) => {
  try {
    const userRef = doc(db, COLLECTION, userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return { success: true, data: { id: userSnap.id, ...userSnap.data() } };
    } else {
      return { success: false, error: 'User not found' };
    }
  } catch (error) {
    console.error('Error getting user:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Atualizar dados do usuário
 */
export const updateUser = async (userId, updates) => {
  try {
    const userRef = doc(db, COLLECTION, userId);
    await updateDoc(userRef, {
      ...updates,
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating user:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Buscar todos os usuários (apenas para admin)
 */
export const getAllUsers = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, COLLECTION));
    
    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: users };
  } catch (error) {
    console.error('Error getting all users:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Buscar usuários por role
 */
export const getUsersByRole = async (role) => {
  try {
    const q = query(collection(db, COLLECTION), where('role', '==', role));
    const querySnapshot = await getDocs(q);
    
    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: users };
  } catch (error) {
    console.error('Error getting users by role:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Buscar dono de uma barbearia específica
 */
export const getOwnerByBarbershopId = async (barbershopId) => {
  try {
    const q = query(
      collection(db, COLLECTION), 
      where('role', '==', 'owner'),
      where('barbershopId', '==', barbershopId)
    );
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { success: true, data: { id: doc.id, ...doc.data() } };
    }
    return { success: false, error: 'Owner not found' };
  } catch (error) {
    console.error('Error getting owner:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Listener real-time para um usuário específico
 */
export const subscribeToUser = (userId, callback) => {
  try {
    const userRef = doc(db, COLLECTION, userId);
    
    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        callback({ id: docSnap.id, ...docSnap.data() });
      } else {
        callback(null);
      }
    });
    
    return unsubscribe;
  } catch (error) {
    console.error('Error subscribing to user:', error);
    return () => {};
  }
};

/**
 * Atualizar perfil profissional do usuário (specialty e experience)
 */
export const updateProfessionalProfile = async (userId, specialty, experience) => {
  try {
    const userRef = doc(db, COLLECTION, userId);
    await updateDoc(userRef, {
      specialty: specialty || '',
      experience: experience || '',
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating professional profile:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Buscar usuários disponíveis para convite (não são donos da barbearia específica)
 */
export const searchAvailableUsers = async (searchTerm, barbershopId, currentUserId) => {
  try {
    // Buscar todos os usuários
    const usersSnapshot = await getDocs(collection(db, COLLECTION));
    
    const availableUsers = [];
    usersSnapshot.forEach((doc) => {
      const userData = doc.data();
      const userName = userData.name?.toLowerCase() || '';
      const userEmail = userData.email?.toLowerCase() || '';
      const search = searchTerm.toLowerCase();
      
      // Verificar se o usuário corresponde à busca
      const matchesSearch = userName.includes(search) || userEmail.includes(search);
      
      // Verificar se o usuário não é dono desta barbearia
      const barbershops = userData.barbershops || [];
      const isNotOwner = !barbershops.includes(barbershopId);
      
      // Verificar se não é o próprio usuário logado
      const isNotSelf = doc.id !== currentUserId;
      
      if (matchesSearch && isNotOwner && isNotSelf) {
        availableUsers.push({
          id: doc.id,
          name: userData.name,
          email: userData.email,
          photoURL: userData.photoURL || null,
          role: userData.role
        });
      }
    });
    
    return { success: true, data: availableUsers };
  } catch (error) {
    console.error('Error searching available users:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Deletar usuário e todos os seus dados relacionados
 * ATENÇÃO: Esta ação é irreversível
 */
export const deleteUserAccount = async (userId) => {
  try {
    // 1. Deletar agendamentos do usuário
    const appointmentsQuery = query(
      collection(db, 'appointments'),
      where('userId', '==', userId)
    );
    const appointmentsSnapshot = await getDocs(appointmentsQuery);
    const deleteAppointmentsPromises = appointmentsSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deleteAppointmentsPromises);

    // 2. Deletar waitlist entries do usuário
    const waitlistQuery = query(
      collection(db, 'waitlist'),
      where('userId', '==', userId)
    );
    const waitlistSnapshot = await getDocs(waitlistQuery);
    const deleteWaitlistPromises = waitlistSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deleteWaitlistPromises);

    // 3. Deletar notificações do usuário (se existir)
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', userId)
    );
    const notificationsSnapshot = await getDocs(notificationsQuery);
    const deleteNotificationsPromises = notificationsSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deleteNotificationsPromises);

    // 4. Deletar convites pendentes do usuário
    const invitationsQuery = query(
      collection(db, 'barbershopInvitations'),
      where('invitedUserId', '==', userId),
      where('status', '==', 'pending')
    );
    const invitationsSnapshot = await getDocs(invitationsQuery);
    const deleteInvitationsPromises = invitationsSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deleteInvitationsPromises);

    // 5. Por último, deletar o documento do usuário
    const userRef = doc(db, COLLECTION, userId);
    await deleteDoc(userRef);

    return { success: true };
  } catch (error) {
    console.error('Error deleting user account:', error);
    return { success: false, error: error.message };
  }
};
